import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import 'focus-visible'

import { HeadlineLevel } from '@aok/text-elements'

import '../../../css/tailwind-base.css'
import '../../../css/tailwind-components.css'
import '../../../css/tailwind-utilities.css'

import { handleTrackingCookie, handleTrackingScript } from '../../../utils/TrackingCookieUtils'
import { PageType, PageCode } from '../../../utils/typeDefinitions/main'

import { clientListPropTypes } from '../../../utils/propTypes/clientList'
import { useJumpLinks } from '../../../hooks/customHooks'

import Layout from '../../_layouts/Layout/Layout'
import Spacer from '../../_layouts/Spacer'

import ModuleSwitch from '../ContentSwitch/ModuleSwitch'

import MacroSwitch from '../ContentSwitch/MacroSwitch'
import ContentHeader from '../../organisms/ContentHeader/ContentHeader'
import T002 from '../../organisms/T002/T002'
import T004 from '../../organisms/T004/T004'
import T005 from '../../organisms/T005/T005'
import T006 from '../../organisms/T006/T006'
import T007 from '../../organisms/T007/T007'
import T008 from '../../organisms/T008/T008'
import T009 from '../../organisms/T009/T009'
import T010 from '../../organisms/T010/T010'
import M046 from '../../organisms/M046/M046'
import HeadElementContent from '../HeadElementContent/HeadElementContent'

const Article = (props) => {
  const { pageContext } = props

  const isGatsby = typeof pageContext !== 'undefined'

  const slug = isGatsby ? pageContext.slug : props.slug
  const surveyEnabled = isGatsby ? pageContext.surveyEnabled : props.surveyEnabled
  const apis = isGatsby ? pageContext.apis : props.apis
  const pageCode = isGatsby ? pageContext.type : props.type
  const meta = isGatsby ? pageContext.meta : props.meta
  const trackingUrl = isGatsby ? pageContext.trackingUrl : props.trackingUrl
  const pageHeader = isGatsby ? pageContext.pageHeader : props.pageHeader
  const primaryContent = isGatsby ? pageContext.primaryContent : props.primaryContent
  const secondaryContent = isGatsby ? pageContext.secondaryContent : props.secondaryContent
  const regional = isGatsby ? pageContext.regional : props.regional
  const t002 = isGatsby ? pageContext.T002 : props.T002
  const t004 = isGatsby ? pageContext.T004 : props.T004
  const t006 = isGatsby ? pageContext.T006 : props.T006
  const t007 = isGatsby ? pageContext.T007 : props.T007
  const t008 = isGatsby ? pageContext.T008 : props.T008
  const t009 = isGatsby ? pageContext.T009 : props.T009
  const t010 = isGatsby ? pageContext.T010 : props.T010
  const m046 = isGatsby ? pageContext.M046 : props.M046
  const y008 = isGatsby ? pageContext.Y008 : props.Y008
  const alertBanner = isGatsby ? pageContext.alertBanner : props.alertBanner
  const staticTexts = isGatsby ? pageContext.staticTexts : props.staticTexts
  const header = isGatsby ? pageContext.header : props.header
  const footer = isGatsby ? pageContext.footer : props.footer
  const clientList = isGatsby ? pageContext.clientList : props.clientList
  const isSignLanguage = isGatsby ? pageContext.isDGSPage : props.isDGSPage

  const releaseDate = isGatsby ? pageContext.releaseDate : props.releaseDate

  const { jumpLinks } = useJumpLinks(primaryContent)

  useEffect(() => {
    if (trackingUrl) {
      handleTrackingScript(trackingUrl)
    }

    handleTrackingCookie()
  }, [trackingUrl])

  return (
    <Layout
      meta={meta}
      header={header}
      footer={footer}
      y008={y008}
      staticTexts={staticTexts}
      surveyEnabled={surveyEnabled}
      apis={apis}
      clientList={clientList}
      regional={regional}
      pageCode={pageCode}
      isSignLanguage={isSignLanguage}
      alertBanner={alertBanner}
    >
      <main id="main-content">
        <ContentHeader {...pageHeader} alertBanner={alertBanner} dataTrackingName="article-header" />

        <HeadlineLevel>
          {t002 && (
            <>
              <Spacer className="h-32 600:h-48 840:h-56" />
              <T002 {...t002} />
            </>
          )}

          {t004 && (
            <>
              <Spacer className="h-32 600:h-48 840:h-56" />
              <T004 {...t004} />
            </>
          )}

          {m046 && (
            <>
              <Spacer className="h-48 600:h-80" />
              <M046 {...m046} className="mt-24 600:mt-40 600:mb-16 1000:mt-48 1000:mb-24" jumpLinks={jumpLinks} />
            </>
          )}

          {primaryContent && !!primaryContent.length && (
            <>
              <Spacer className="h-48 600:h-80" />

              <MacroSwitch content={primaryContent} />
            </>
          )}

          {t009?.nextArticles?.topicUrl && (
            <>
              <Spacer className="h-48 600:h-80" />
              <T009 {...t009.nextArticles} />
            </>
          )}

          {(t007?.author || t007?.publisher || t008?.contact) && t006 && (
            <>
              <Spacer className="h-48 600:h-80" />
              {(t007?.author || t007?.publisher) && <T007 {...t007} />}
              {t008?.contact && <T008 {...t008} />}
              <Spacer className="h-40" />
              <T006 {...t006} releaseDate={releaseDate} />
              <Spacer className="h-32" />
              <T005 overlayData={staticTexts?.privacy?.socialMedia} targetId="#main-content" />
            </>
          )}

          {!t007 && !t008 && t006 && (
            <>
              <Spacer className="h-48 600:h-80" />
              <T006 {...t006} releaseDate={releaseDate} />
              <Spacer className="h-32" />
              <T005 overlayData={staticTexts?.privacy?.socialMedia} targetId="#main-content" />
            </>
          )}

          {!t007 && !t008 && !t006 && (
            <>
              <Spacer className="h-48 600:h-80" />
              <T005 overlayData={staticTexts?.privacy?.socialMedia} targetId="#main-content" />
            </>
          )}

          {/* ToDo: T016 - Disclaimer Text */}

          {t010 && (
            <>
              <Spacer className="h-64 600:h-80 1000:h-112" />
              <T010 {...Object.assign({ slug }, t010)} />
            </>
          )}
        </HeadlineLevel>

        {secondaryContent && !!secondaryContent.length && (
          <>
            <Spacer className="h-64 600:h-96 840:h-112" />

            <HeadlineLevel>
              <ModuleSwitch content={secondaryContent} isSecondaryContent={true} />
            </HeadlineLevel>
          </>
        )}
        {!secondaryContent && <Spacer className="h-64 1280:h-96" />}
      </main>
    </Layout>
  )
}

Article.propTypes = {
  id: PropTypes.number,
  surveyEnabled: PropTypes.bool,
  apis: PropTypes.object,
  footer: PropTypes.object,
  googleSite: PropTypes.string,
  header: PropTypes.object,
  M046: PropTypes.object,
  meta: PropTypes.object,
  pageHeader: PropTypes.object,
  pageType: PropTypes.oneOf(Object.values(PageType)),
  pageCode: PropTypes.oneOf(Object.values(PageCode)),
  primaryContent: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ),
  staticTexts: PropTypes.object,
  clientList: clientListPropTypes,
  releaseDate: PropTypes.string,
  secondaryContent: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
    })
  ),
  regional: PropTypes.bool,
  siteUrl: PropTypes.string,
  slug: PropTypes.string,
  T002: PropTypes.object,
  T004: PropTypes.object,
  T006: PropTypes.object,
  T007: PropTypes.object,
  T008: PropTypes.object,
  T009: PropTypes.object,
  T010: PropTypes.object,
  trackingUrl: PropTypes.string,
  Y008: PropTypes.object,
}

// named export for Gatsby Head API (see https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/)
export const Head = (props) => <HeadElementContent {...props} />

export default Article
